// src/modules/stock/Scanner/HistoriqueScans.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
    Table, Badge, Spinner, Alert,
    Form, InputGroup, Pagination
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { formatDate } from "../../../utils/formatters";

// ✅ Utiliser forwardRef pour exposer la méthode refresh
const HistoriqueScans = forwardRef((props, ref) => {
    const [historique, setHistorique] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);

    const getToken = () => {
        return localStorage.getItem("token");
    };

    // ✅ Exposer la méthode refresh au parent
    useImperativeHandle(ref, () => ({
        refresh: () => {
            fetchHistorique();
        }
    }));

    useEffect(() => {
        fetchHistorique();
    }, []);

    const fetchHistorique = async () => {
        try {
            setLoading(true);
            const token = getToken();
            
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/scanner.php?action=historique&limite=100',
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setHistorique(result.data);
                setError(null);
            } else {
                setError(result.message || "Erreur chargement historique");
            }
        } catch (err) {
            setError(err.message || "Erreur chargement historique");
            console.error("Erreur historique:", err);
        } finally {
            setLoading(false);
        }
    };

    // Filtrage
    const filteredHistorique = historique.filter(item =>
        item.produit_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.code_barre?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.commentaire?.toLowerCase().includes(filterText.toLowerCase())
    );

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredHistorique.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredHistorique.length / itemsPerPage);

    const getTypeIcon = (type) => {
        const icons = {
            entree: 'ri-add-circle-line text-white',
            sortie: 'ri-subtract-line text-white',
            inventaire: 'ri-file-list-line text-white',
            annulation: 'ri-close-circle-line text-white'
        };
        return icons[type] || 'ri-question-line';
    };

    const getTypeLabel = (type) => {
        const labels = {
            entree: 'Entrée',
            sortie: 'Sortie',
            inventaire: 'Inventaire',
            annulation: 'Annulation'
        };
        return labels[type] || type;
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    if (loading && historique.length === 0) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error && historique.length === 0) {
        return (
            <Alert variant="danger" className="m-3">
                <i className="ri-error-warning-line me-2"></i>
                {error}
            </Alert>
        );
    }

    return (
        <div className="p-3">
            {/* Filtre */}
            <div className="mb-3">
                <InputGroup>
                    <InputGroup.Text>
                        <i className="ri-search-line"></i>
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Rechercher dans l'historique..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </InputGroup>
            </div>

            {/* Tableau */}
            <div className="table-responsive">
                <Table hover>
                    <thead>
                        <tr>
                            <th>Date/Heure</th>
                            <th>Produit</th>
                            <th>Type</th>
                            <th>Qté</th>
                            <th>Stock avant</th>
                            <th>Stock après</th>
                            <th>Utilisateur</th>
                            <th>Commentaire</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <div>{formatDate(item.date_mouvement)}</div>
                                        <small className="text-muted">
                                            {new Date(item.date_mouvement).toLocaleTimeString()}
                                        </small>
                                    </td>
                                    <td>
                                        <Link to={`/stock/produits/${item.id_produit}`} className="text-primary">
                                            <strong>{item.produit_nom}</strong>
                                        </Link>
                                        <div>
                                            <small className="text-muted">{item.reference}</small>
                                        </div>
                                        {item.code_barre && (
                                            <small className="text-muted">
                                                <i className="ri-qr-code-line me-1"></i>
                                                {item.code_barre}
                                            </small>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={item.type_mouvement === 'entree' ? 'success' : 'danger'}>
                                            <i className={`${getTypeIcon(item.type_mouvement)} me-1`}></i>
                                            {getTypeLabel(item.type_mouvement)}
                                        </Badge>
                                    </td>
                                    <td className="fw-bold">
                                        <span className={item.type_mouvement === 'entree' ? 'text-success' : 'text-danger'}>
                                            {item.type_mouvement === 'entree' ? '+' : '-'}{item.quantite}
                                        </span>
                                    </td>
                                    <td>{item.stock_avant}</td>
                                    <td>{item.stock_apres}</td>
                                    <td>
                                       {item.utilisateur_nom}
                                    </td>
                                    <td>
                                        <small className="text-muted">{item.commentaire}</small>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center py-4">
                                    <i className="ri-history-line fs-1 text-muted mb-3 d-block"></i>
                                    <h6>Aucun scan dans l'historique</h6>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                        {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredHistorique.length)} sur {filteredHistorique.length}
                    </small>
                    <Pagination>
                        <Pagination.Prev 
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                        />
                        {[...Array(totalPages)].map((_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === currentPage}
                                onClick={() => handlePageChange(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next 
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </div>
            )}
        </div>
    );      
});

export default HistoriqueScans;